// Cartes Cadeaux - Page pour les ACHETEURS
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Gift, ArrowLeft, CreditCard, Mail, User, ChevronRight,
  Check, ShoppingCart, Sparkles, Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Montants disponibles
const AMOUNTS = [25, 50, 75, 100, 150, 200];

// Designs de cartes
const CARD_DESIGNS = [
  { id: 'classic', name: 'Classique', gradient: 'from-orange-500 to-red-500' },
  { id: 'christmas', name: 'Noël', gradient: 'from-red-600 to-green-600' },
  { id: 'birthday', name: 'Anniversaire', gradient: 'from-pink-500 to-purple-500' },
  { id: 'thankyou', name: 'Merci', gradient: 'from-blue-500 to-cyan-500' },
  { id: 'love', name: 'Amour', gradient: 'from-red-500 to-pink-500' },
  { id: 'pro', name: 'Pro', gradient: 'from-gray-700 to-gray-900' },
];

// Carte cadeau preview
function GiftCardPreview({ design, amount, recipientName }) {
  const designData = CARD_DESIGNS.find(d => d.id === design) || CARD_DESIGNS[0];

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`relative aspect-[1.6/1] rounded-2xl bg-gradient-to-br ${designData.gradient} p-6 text-white shadow-xl overflow-hidden`}
    >
      {/* Pattern décoratif */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-4 right-4">
          <Sparkles size={48} />
        </div>
        <div className="absolute bottom-4 left-4">
          <Gift size={64} />
        </div>
      </div>

      {/* Contenu */}
      <div className="relative h-full flex flex-col justify-between">
        <div>
          <p className="text-white/80 text-sm">Carte cadeau</p>
          <h3 className="text-2xl font-bold">AbracadaBati</h3>
        </div>

        <div>
          {recipientName && (
            <p className="text-white/80 text-sm mb-1">Pour: {recipientName}</p>
          )}
          <p className="text-4xl font-black">{amount} €</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function CartesPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedDesign, setSelectedDesign] = useState('classic');
  const [form, setForm] = useState({
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: ''
  });

  const finalAmount = customAmount ? parseInt(customAmount) : selectedAmount;

  const handleAddToCart = () => {
    if (!form.recipientName || !form.recipientEmail) {
      toast.error('Veuillez remplir les informations du destinataire');
      return;
    }
    toast.success(`Carte cadeau de ${finalAmount}€ ajoutée au panier !`);
    navigate('/produits');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cartes-cadeaux-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Gift size={24} className="text-orange-500" />
                Cartes cadeaux
              </h1>
              <p className="text-sm text-gray-500">Offrez une carte cadeau AbracadaBati</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Formulaire */}
          <div className="space-y-6">
            {/* Étape 1: Montant */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center">1</span>
                Choisir le montant
              </h2>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                {AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => { setSelectedAmount(amount); setCustomAmount(''); }}
                    className={`py-3 px-4 rounded-xl font-bold text-lg transition-all ${
                      selectedAmount === amount && !customAmount
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {amount} €
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Ou montant personnalisé (10€ - 500€)</label>
                <Input
                  type="number"
                  min={10}
                  max={500}
                  placeholder="Montant"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Étape 2: Design */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center">2</span>
                Choisir le design
              </h2>
              
              <div className="grid grid-cols-3 gap-3">
                {CARD_DESIGNS.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => setSelectedDesign(design.id)}
                    className={`relative p-3 rounded-xl transition-all ${
                      selectedDesign === design.id
                        ? 'ring-2 ring-orange-500 ring-offset-2'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`h-8 rounded-lg bg-gradient-to-r ${design.gradient}`} />
                    <p className="text-xs text-gray-600 mt-2">{design.name}</p>
                    {selectedDesign === design.id && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Étape 3: Destinataire */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center">3</span>
                Informations du destinataire
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du destinataire</label>
                  <Input
                    value={form.recipientName}
                    onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
                    placeholder="Prénom et nom"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email du destinataire</label>
                  <Input
                    type="email"
                    value={form.recipientEmail}
                    onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })}
                    placeholder="email@exemple.com"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Votre nom (optionnel)</label>
                  <Input
                    value={form.senderName}
                    onChange={(e) => setForm({ ...form, senderName: e.target.value })}
                    placeholder="De la part de..."
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Ajoutez un message personnel..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview et résumé */}
          <div className="lg:sticky lg:top-24 h-fit space-y-6">
            {/* Preview de la carte */}
            <GiftCardPreview
              design={selectedDesign}
              amount={finalAmount}
              recipientName={form.recipientName}
            />

            {/* Résumé */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Résumé</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Carte cadeau</span>
                  <span className="font-medium">{finalAmount} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Livraison</span>
                  <span className="font-medium text-green-600">Gratuite (email)</span>
                </div>
                <div className="border-t border-gray-100 pt-3 flex justify-between">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-bold text-xl text-orange-600">{finalAmount} €</span>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl py-3"
              >
                <ShoppingCart size={18} className="mr-2" />
                Ajouter au panier
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                La carte sera envoyée par email immédiatement après l'achat
              </p>
            </div>

            {/* Infos */}
            <div className="bg-orange-50 rounded-xl p-4">
              <h4 className="font-medium text-orange-800 mb-2">Comment ça marche ?</h4>
              <ul className="text-sm text-orange-700 space-y-1">
                <li>• Choisissez le montant et le design</li>
                <li>• Le destinataire reçoit la carte par email</li>
                <li>• Il peut l'utiliser sur tout le site</li>
                <li>• Valable 1 an, utilisable en plusieurs fois</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
